import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Cookie } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useConsent } from "@/hooks/useConsent";
import { CONSENT_EVENT, CONSENT_OPEN_EVENT, hasDecided, readConsent, writeConsent } from "@/lib/consent";

const CookieConsent = () => {
  const { acceptAll, acceptEssentialOnly } = useConsent();
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  // Local draft so toggling a switch doesn't immediately mark a decision
  // (which would auto-close the banner). Persisted only on "Salvează alegerea".
  const [draft, setDraft] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    const syncDraft = () => {
      const current = readConsent();
      setDraft({ analytics: current.analytics, marketing: current.marketing });
    };

    const evaluate = () => {
      const current = readConsent();
      if (!hasDecided(current)) {
        syncDraft();
        const t = setTimeout(() => setVisible(true), 600);
        return () => clearTimeout(t);
      }
      setVisible(false);
    };

    const cleanup = evaluate();
    const handler = () => evaluate();
    const openHandler = () => {
      syncDraft();
      setShowDetails(true);
      setVisible(true);
    };

    window.addEventListener(CONSENT_EVENT, handler);
    window.addEventListener(CONSENT_OPEN_EVENT, openHandler);
    return () => {
      cleanup?.();
      window.removeEventListener(CONSENT_EVENT, handler);
      window.removeEventListener(CONSENT_OPEN_EVENT, openHandler);
    };
  }, []);

  if (!visible) return null;

  const saveChoice = () => {
    writeConsent({ analytics: draft.analytics, marketing: draft.marketing });
    toast.success("Preferințele tale au fost salvate.");
  };

  const handleAcceptAll = () => {
    acceptAll();
    toast.success("Ai acceptat toate cookie-urile.");
  };

  const handleEssentialOnly = () => {
    acceptEssentialOnly();
    toast.success("Salvat: doar cookie-uri esențiale.");
  };

  return (
    <div
      role="dialog"
      aria-label="Setări cookie-uri"
      aria-modal="false"
      className="fixed bottom-0 inset-x-0 z-50 p-4 animate-in slide-in-from-bottom-4 duration-500"
    >
      <div className="container max-w-2xl">
        <div className="rounded-xl border border-border bg-card shadow-lg p-5 space-y-4">
          <div className="flex items-start gap-3">
            <Cookie className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground">Cookie-uri și confidențialitate</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Folosim doar cookie-uri esențiale pentru autentificare și funcționarea platformei.
                Categoriile suplimentare (analitice, marketing) sunt dezactivate implicit și nu se
                încarcă fără acordul tău. Detalii în{" "}
                <a href="/privacy" className="text-primary hover:underline">
                  Politica de Confidențialitate
                </a>
                .
              </p>
            </div>
          </div>

          {showDetails && (
            <div className="space-y-3 rounded-lg border border-border bg-muted/30 p-3">
              <Row
                title="Esențiale"
                desc="Sesiune autentificare, preferințe UI. Obligatorii."
                checked
                disabled
              />
              <Row
                title="Analitice"
                desc="Statistici anonime de utilizare (momentan neactivat)."
                checked={draft.analytics}
                onChange={(v) => setDraft((d) => ({ ...d, analytics: v }))}
              />
              <Row
                title="Marketing"
                desc="Retargeting, reclame personalizate (momentan neactivat)."
                checked={draft.marketing}
                onChange={(v) => setDraft((d) => ({ ...d, marketing: v }))}
              />
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2 sm:justify-between sm:items-center">
            <button
              type="button"
              onClick={() => setShowDetails((s) => !s)}
              className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
            >
              {showDetails ? "Ascunde detalii" : "Personalizează"}
            </button>
            <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
              <Button variant="outline" size="sm" onClick={handleEssentialOnly} className="text-xs">
                Doar esențiale
              </Button>
              {showDetails ? (
                <Button size="sm" onClick={saveChoice} className="text-xs">
                  Salvează alegerea
                </Button>
              ) : (
                <Button size="sm" onClick={handleAcceptAll} className="text-xs">
                  Acceptă toate
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Row = ({
  title,
  desc,
  checked,
  disabled,
  onChange,
}: {
  title: string;
  desc: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: boolean) => void;
}) => (
  <div className="flex items-start justify-between gap-3">
    <div className="space-y-0.5">
      <Label className="text-xs font-medium text-foreground">{title}</Label>
      <p className="text-xs text-muted-foreground leading-relaxed">{desc}</p>
    </div>
    <Switch checked={checked} disabled={disabled} onCheckedChange={onChange} />
  </div>
);

export default CookieConsent;
